import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Building2, Users, Calendar, DollarSign, Target, Loader2, AlertCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { getUserId, getAuthUser } from "@/lib/subscription";

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_OPTIONS = [
  { id: "risk", label: "Risk Mitigation", description: "Minimize downside" },
  { id: "growth", label: "Growth Focus", description: "Maximize expansion" },
  { id: "speed", label: "Speed to Market", description: "Time-critical execution" },
  { id: "cost", label: "Cost Efficiency", description: "Optimize resources" }
];

export default function EnterpriseDecisionPage() {
  const navigate = useNavigate();
  const [decisionText, setDecisionText] = useState("");
  const [department, setDepartment] = useState("");
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("");
  const [priority, setPriority] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEnterprise, setIsEnterprise] = useState(true);
  const [isTrial, setIsTrial] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      const user = getAuthUser();
      const userId = getUserId();
      
      if (user?.plan === 'enterprise' && user?.is_premium) {
        setIsEnterprise(true);
        setIsTrial(false);
        return;
      }
      
      // Check trial availability
      try {
        const res = await axios.get(`${API}/api/enterprise/trial-status/${userId}`);
        if (res.data.trial_available) {
          setIsEnterprise(true);
          setIsTrial(true);
        } else {
          setIsEnterprise(false);
        }
      } catch {
        setIsEnterprise(false);
      }
    };
    checkAccess();
  }, []);

  const handleSubmit = async () => {
    setError(null);
    
    // Validation
    if (!decisionText.trim()) {
      toast.error("Please describe your business decision");
      return;
    }
    if (decisionText.trim().length < 10) {
      toast.error("Please provide more details about your decision");
      return;
    }
    if (!priority) {
      toast.error("Please select a priority focus");
      return;
    }

    setIsLoading(true);
    
    try {
      const userId = getUserId();
      
      // Log payload for debugging
      console.log("Enterprise decision payload:", {
        decision_text: decisionText.trim(),
        department: department.trim() || null,
        budget: budget.trim() || null,
        timeline: timeline.trim() || null,
        priority: priority,
        user_id: userId
      });
      
      const response = await axios.post(`${API}/api/enterprise/decision`, {
        decision_text: decisionText.trim(),
        department: department.trim() || null,
        budget: budget.trim() || null,
        timeline: timeline.trim() || null,
        priority: priority,
        user_id: userId
      });

      if (response.data.session_id) {
        navigate(`/enterprise/analysis/${response.data.session_id}`);
      } else if (response.data.is_harmful) {
        // Safety response
        toast.info(response.data.message);
      } else {
        setError("Analysis could not be started. Please try again.");
      }
    } catch (error) {
      console.error("Enterprise decision error:", error);
      
      // Handle specific error codes
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 403) {
        setError("Enterprise subscription required. Please upgrade to access this feature.");
        setIsEnterprise(false);
      } else if (status === 404) {
        setError("User account not found. Please sign in again.");
      } else if (status === 429) {
        setError(detail || "Weekly limit reached. Please try again next week.");
      } else if (detail) {
        setError(detail);
      } else {
        setError("Unable to process your request. Please check your connection and try again.");
      }
      
      toast.error(error.response?.data?.detail || "Analysis failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Show upgrade prompt if not enterprise
  if (!isEnterprise) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4 max-w-md">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
          <h2 className="font-heading text-xl text-foreground">Enterprise Access Required</h2>
          <p className="text-sm text-muted-foreground">
            The Enterprise Decision Engine is available with the ₹999/month plan.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              Go Back
            </Button>
            <Button onClick={() => navigate("/pricing")}>
              View Plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/60 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/enterprise/history")}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="enterprise-history-btn"
            >
              <Clock className="h-3.5 w-3.5" />
              History
            </button>
            <span className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary">
              {isTrial ? "Enterprise Trial" : "Enterprise"}
            </span>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-12 px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Title */}
            <div className="text-center space-y-2">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground">
                Enterprise Decision Engine
              </h1>
              <p className="text-sm text-muted-foreground">
                Strategic analysis with department dependencies and priority weighting
              </p>
            </div>

            {/* Trial info banner */}
            {isTrial && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-center" data-testid="enterprise-trial-banner">
                <p className="text-xs text-primary">
                  One-time free Enterprise trial — full analysis, no restrictions
                </p>
              </div>
            )}

            {/* Main Input */}
            <div className="floating-card p-6 space-y-6">
              {/* Decision Text */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Describe your business decision
                </label>
                <textarea
                  value={decisionText}
                  onChange={(e) => setDecisionText(e.target.value)}
                  placeholder="E.g., Should we expand into the European market this quarter, or focus on consolidating our US operations first?"
                  className="w-full h-32 px-4 py-3 bg-secondary/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="enterprise-decision-input"
                />
              </div>

              {/* Optional Fields */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Users className="h-3 w-3" />
                    Department / Team
                  </label>
                  <input
                    type="text"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="e.g., Operations"
                    className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <DollarSign className="h-3 w-3" />
                    Budget / Scale
                  </label>
                  <input
                    type="text"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    placeholder="e.g., $500K"
                    className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" />
                    Timeline
                  </label>
                  <input
                    type="text"
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value)}
                    placeholder="e.g., Q2 2026"
                    className="w-full px-3 py-2 bg-secondary/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Priority Selector */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  Priority Focus
                  <span className="text-xs text-muted-foreground">(Required)</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {PRIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setPriority(opt.id)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        priority === opt.id
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      }`}
                      data-testid={`priority-${opt.id}`}
                    >
                      <p className={`text-sm font-medium ${priority === opt.id ? "text-primary" : "text-foreground"}`}>
                        {opt.label}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {opt.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={isLoading || !decisionText.trim() || !priority}
                className="w-full h-12 rounded-full text-sm font-medium"
                data-testid="run-enterprise-analysis"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Running Analysis...
                  </>
                ) : (
                  "Run Enterprise Analysis"
                )}
              </Button>
            </div>

            {/* Info Note */}
            <p className="text-xs text-muted-foreground text-center max-w-md mx-auto">
              Enterprise analysis includes custom decision modeling, role-based alignment, 
              and strategic next steps tailored to your priority focus.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
